import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useBranch } from "../../../contexts/BranchContext";
import InlinePaymentForm from "./InlinePaymentForm";

const statusConfig = {
  paid: { label: "Paid", color: "var(--color-success)", bg: "var(--color-success)" },
  partial: { label: "Partial", color: "var(--color-warning)", bg: "var(--color-warning)" },
  overdue: { label: "Overdue", color: "var(--color-error)", bg: "var(--color-error)" },
  pending: { label: "Pending", color: "var(--color-primary)", bg: "var(--color-primary)" },
};

const paymentMethodOptions = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

const downloadReceipt = async (payment, getMemberName) => {
  try {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "a5" });

    const memberName = getMemberName(payment);
    const memberEmail = payment?.member_email || "—";
    const amount = parseFloat(payment?.amount || 0)?.toFixed(2);
    const paidAmount = parseFloat(payment?.paid_amount || 0)?.toFixed(2);
    const paymentType = payment?.payment_status === "paid" ? "Full" : "Partial";
    const paymentMethod = payment?.payment_method
      ? payment?.payment_method?.charAt(0)?.toUpperCase() + payment?.payment_method?.slice(1)
      : "—";
    const paymentDate = payment?.paid_date
      ? new Date(payment.paid_date)?.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
      : payment?.created_at
      ? new Date(payment.created_at)?.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
      : "—";
    const invoiceNumber = `INV-${String(payment?.id || "")?.slice(-8)?.toUpperCase()}`;
    const gymName = "Gym Vendor Portal";

    const pageWidth = doc?.internal?.pageSize?.getWidth();
    const margin = 36;
    let y = 40;

    doc?.setFillColor(30, 30, 60);
    doc?.rect(0, 0, pageWidth, 80, "F");
    doc?.setFont("helvetica", "bold");
    doc?.setFontSize(18);
    doc?.setTextColor(255, 255, 255);
    doc?.text(gymName, pageWidth / 2, 32, { align: "center" });
    doc?.setFont("helvetica", "normal");
    doc?.setFontSize(10);
    doc?.setTextColor(180, 180, 220);
    doc?.text("PAYMENT RECEIPT", pageWidth / 2, 52, { align: "center" });
    doc?.setFontSize(9);
    doc?.setTextColor(140, 140, 180);
    doc?.text(invoiceNumber, pageWidth / 2, 68, { align: "center" });

    y = 100;
    doc?.setDrawColor(220, 220, 230);
    doc?.setLineWidth(0.5);
    doc?.line(margin, y, pageWidth - margin, y);
    y += 16;

    doc?.setFont("helvetica", "bold");
    doc?.setFontSize(9);
    doc?.setTextColor(100, 100, 130);
    doc?.text("BILLED TO", margin, y);
    y += 14;
    doc?.setFont("helvetica", "bold");
    doc?.setFontSize(11);
    doc?.setTextColor(30, 30, 60);
    doc?.text(memberName, margin, y);
    y += 13;
    doc?.setFont("helvetica", "normal");
    doc?.setFontSize(9);
    doc?.setTextColor(80, 80, 100);
    doc?.text(memberEmail, margin, y);
    y += 20;

    doc?.setDrawColor(220, 220, 230);
    doc?.line(margin, y, pageWidth - margin, y);
    y += 16;

    const rows = [
      ["Invoice Number", invoiceNumber],
      ["Payment Date", paymentDate],
      ["Payment Method", paymentMethod],
      ["Payment Type", paymentType],
    ];

    doc?.setFontSize(9);
    rows?.forEach(([label, value]) => {
      doc?.setFont("helvetica", "normal");
      doc?.setTextColor(100, 100, 130);
      doc?.text(label, margin, y);
      doc?.setFont("helvetica", "bold");
      doc?.setTextColor(30, 30, 60);
      doc?.text(value, pageWidth - margin, y, { align: "right" });
      y += 16;
    });

    y += 4;
    doc?.setFillColor(245, 245, 252);
    doc?.roundedRect(margin, y, pageWidth - margin * 2, 64, 4, 4, "F");

    y += 16;
    doc?.setFontSize(9);
    doc?.setFont("helvetica", "normal");
    doc?.setTextColor(100, 100, 130);
    doc?.text("Total Amount", margin + 12, y);
    doc?.setFont("helvetica", "bold");
    doc?.setTextColor(30, 30, 60);
    doc?.text(`$${amount}`, pageWidth - margin - 12, y, { align: "right" });
    y += 16;

    doc?.setFont("helvetica", "normal");
    doc?.setTextColor(100, 100, 130);
    doc?.text("Amount Paid", margin + 12, y);
    doc?.setFont("helvetica", "bold");
    doc?.setTextColor(34, 139, 34);
    doc?.text(`$${paidAmount}`, pageWidth - margin - 12, y, { align: "right" });
    y += 16;

    const balance = (parseFloat(payment?.amount || 0) - parseFloat(payment?.paid_amount || 0))?.toFixed(2);
    if (parseFloat(balance) > 0) {
      doc?.setFont("helvetica", "normal");
      doc?.setTextColor(100, 100, 130);
      doc?.text("Balance Due", margin + 12, y);
      doc?.setFont("helvetica", "bold");
      doc?.setTextColor(200, 50, 50);
      doc?.text(`$${balance}`, pageWidth - margin - 12, y, { align: "right" });
    }

    y += 24;
    doc?.setDrawColor(220, 220, 230);
    doc?.line(margin, y, pageWidth - margin, y);
    y += 16;

    doc?.setFont("helvetica", "normal");
    doc?.setFontSize(8);
    doc?.setTextColor(150, 150, 170);
    doc?.text("Thank you for your payment!", pageWidth / 2, y, { align: "center" });
    y += 12;
    doc?.text(`Generated on ${new Date()?.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`, pageWidth / 2, y, { align: "center" });

    doc?.save(`Receipt_${invoiceNumber}.pdf`);
  } catch (err) {
    console.error("Error generating receipt:", err);
  }
};

function PayRemainingModal({ payment, memberName, onClose, onSaved }) {
  useAuth();
  const pendingAmt = Math.max(0, (parseFloat(payment?.amount) || 0) - (parseFloat(payment?.paid_amount) || 0));
  const [payForm, setPayForm] = useState({
    amount: pendingAmt?.toFixed(2),
    paymentMethod: payment?.payment_method || "cash",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'amount' && value !== '') {
      const num = parseFloat(value);
      if (!isNaN(num) && num > pendingAmt) {
        setPayForm(prev => ({ ...prev, amount: pendingAmt.toFixed(2) }));
        return;
      }
    }
    setPayForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setSaving(true);
      setErr(null);
      const newPaid = parseFloat(payForm?.amount) || 0;
      const totalAmount = parseFloat(payment?.amount) || 0;
      // Use existing paid_amount; fall back to amount - pendingAmt in case paid_amount is 0/null
      const existingPaid = parseFloat(payment?.paid_amount) || (totalAmount - pendingAmt);
      const totalPaid = Math.min(totalAmount, existingPaid + newPaid);
      const newStatus = totalPaid >= totalAmount ? "paid" : "partial";

      await api.put(`/payments/${payment?.id}`, {
        paid_amount: totalPaid,
        payment_status: newStatus,
        payment_method: payForm?.paymentMethod,
        notes: payForm?.notes || payment?.notes || null,
        paid_date: new Date()?.toISOString()?.split('T')?.[0],
      });

      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(e?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div>
            <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Pay Remaining Balance</h3>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{memberName}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="X" size={15} color="var(--color-muted-foreground)" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {err && (
            <div className="flex items-center gap-2 p-2.5 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
              <Icon name="AlertCircle" size={13} color="var(--color-error)" />
              <span className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{err}</span>
            </div>
          )}
          <div className="p-3 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/20 rounded-md">
            <p className="text-xs text-[var(--color-warning)] font-medium font-[var(--font-caption)]">
              Outstanding balance: <strong>${pendingAmt?.toFixed(2)}</strong>
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Amount to Pay <span className="text-[var(--color-error)]">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--color-muted-foreground)] font-[var(--font-data)]">$</span>
              <input
                required
                type="number"
                name="amount"
                value={payForm?.amount}
                onChange={handleChange}
                min="0.01"
                max={pendingAmt}
                step="0.01"
                className="w-full pl-6 pr-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Payment Method</label>
            <div className="grid grid-cols-2 gap-2">
              {paymentMethodOptions?.map(({ value, label }) => (
                <label key={value} className={`flex items-center justify-center gap-1.5 py-2 rounded-md border cursor-pointer text-xs font-medium font-[var(--font-caption)] transition-colors ${
                  payForm?.paymentMethod === value
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                    : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/40"
                }`}>
                  <input type="radio" name="paymentMethod" value={value} checked={payForm?.paymentMethod === value} onChange={handleChange} className="sr-only" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">Notes</label>
            <input
              type="text"
              name="notes"
              value={payForm?.notes}
              onChange={handleChange}
              placeholder="Optional notes..."
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 text-xs font-medium border border-[var(--color-border)] text-[var(--color-muted-foreground)] rounded-md hover:bg-[var(--color-muted)] transition-colors font-[var(--font-caption)]">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 py-2 text-xs font-semibold bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60 font-[var(--font-caption)]">
              {saving ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PaymentTracker() {
  useAuth();
  const { selectedBranchId } = useBranch();
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payRemainingTarget, setPayRemainingTarget] = useState(null);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit: 500 };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get('/payments', { params });
      setPayments(data || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [selectedBranchId]);

  const getMemberName = (payment) => payment?.member_name || 'Unknown';

  const getDaysAging = (payment) => {
    if (payment?.payment_status === 'paid') return 0;
    if (!payment?.due_date) return 0;
    const due = new Date(payment?.due_date);
    const now = new Date();
    const diff = Math.floor((now - due) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const filtered = payments?.filter(p => {
    const matchFilter = filter === "all" || p?.payment_status === filter;
    const memberName = getMemberName(p);
    const matchSearch = memberName?.toLowerCase()?.includes(search?.toLowerCase());
    return matchFilter && matchSearch;
  });

  const summary = {
    total: payments?.reduce((s, p) => s + (parseFloat(p?.amount) || 0), 0),
    collected: payments?.reduce((s, p) => s + (parseFloat(p?.paid_amount) || 0), 0),
    overdue: payments?.filter(p => p?.payment_status === "overdue")?.length,
    partial: payments?.filter(p => p?.payment_status === "partial")?.length,
  };

  if (loading) {
    return (
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] p-8 flex items-center justify-center">
        <div className="flex items-center gap-2 text-[var(--color-muted-foreground)]">
          <Icon name="Loader2" size={16} color="currentColor" className="animate-spin" />
          <span className="text-sm font-[var(--font-caption)]">Loading payments...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {payRemainingTarget && (
        <PayRemainingModal
          payment={payRemainingTarget}
          memberName={getMemberName(payRemainingTarget)}
          onClose={() => setPayRemainingTarget(null)}
          onSaved={fetchPayments}
        />
      )}
      {/* <InlinePaymentForm onPaymentSaved={fetchPayments} /> */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
        <div className="p-4 border-b border-[var(--color-border)]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Payment Tracking</h3>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                Collected ${summary?.collected?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} of ${summary?.total?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} total
              </p>
            </div>
            <div className="relative">
              <Icon name="Search" size={13} color="var(--color-muted-foreground)" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input type="text" placeholder="Search member..." value={search} onChange={e => setSearch(e?.target?.value)} className="pl-7 pr-3 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] w-44" />
            </div>
          </div>
          {error && <p className="text-xs text-[var(--color-error)] mb-2 font-[var(--font-caption)]">{error}</p>}
          <div className="flex flex-wrap gap-2">
            {[["all","All",null],["paid","Paid","var(--color-success)"],["partial","Partial","var(--color-warning)"],["overdue",`Overdue (${summary?.overdue})`,"var(--color-error)"],["pending","Pending","var(--color-primary)"]]?.map(([val, label]) => (
              <button key={val} onClick={() => setFilter(val)} className={`px-2.5 py-1 rounded-full text-xs font-medium font-[var(--font-caption)] transition-colors ${
                filter === val ? "bg-[var(--color-primary)] text-white" : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-border)]"
              }`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          {filtered?.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              No payments found
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--color-muted)] border-b border-[var(--color-border)]">
                  {["Member","Description","Amount","Paid","Pending","Due Date","Status","Aging","Action"]?.map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {filtered?.map(p => {
                  const pendingAmt = Math.max(0, (parseFloat(p?.amount) || 0) - (parseFloat(p?.paid_amount) || 0));
                  const cfg = statusConfig?.[p?.payment_status];
                  const aging = getDaysAging(p);
                  const isPartial = p?.payment_status === "partial";
                  return (
                    <tr key={p?.id} className="hover:bg-[var(--color-muted)]/30 transition-colors">
                      <td className="px-4 py-3 font-medium text-[var(--color-foreground)] font-[var(--font-body)] whitespace-nowrap">{getMemberName(p)}</td>
                      <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{p?.description || '—'}</td>
                      <td className="px-4 py-3 font-[var(--font-data)] text-[var(--color-foreground)]">${parseFloat(p?.amount || 0)?.toFixed(0)}</td>
                      <td className="px-4 py-3 font-[var(--font-data)] text-[var(--color-success)]">${parseFloat(p?.paid_amount || 0)?.toFixed(0)}</td>
                      <td className="px-4 py-3">
                        {isPartial && pendingAmt > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold font-[var(--font-data)]" style={{ backgroundColor: "var(--color-warning)", color: "white", opacity: 0.9 }}>
                            <Icon name="Clock" size={10} color="white" />
                            ${pendingAmt?.toFixed(0)}
                          </span>
                        ) : (
                          <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">
                        {p?.due_date ? new Date(p?.due_date)?.toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cfg?.bg }}></span>
                          <span className="text-xs font-medium font-[var(--font-caption)]" style={{ color: cfg?.color }}>{cfg?.label}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {aging > 0 ? (
                          <span className="text-xs font-medium font-[var(--font-data)]" style={{ color: aging > 7 ? "var(--color-error)" : "var(--color-warning)" }}>{aging}d</span>
                        ) : <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {isPartial && pendingAmt > 0 && (
                            <button
                              onClick={() => setPayRemainingTarget(p)}
                              className="px-2 py-1 text-xs font-semibold bg-[var(--color-warning)]/15 text-[var(--color-warning)] rounded hover:bg-[var(--color-warning)]/30 transition-colors font-[var(--font-caption)] whitespace-nowrap"
                            >
                              Pay Remaining
                            </button>
                          )}
                          {p?.payment_status !== "paid" && !isPartial && (
                            <button className="px-2 py-1 text-xs font-medium bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded hover:bg-[var(--color-primary)]/20 transition-colors font-[var(--font-caption)]">Remind</button>
                          )}
                          <button
                            onClick={() => downloadReceipt(p, getMemberName)}
                            title="Download Receipt"
                            className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors"
                          >
                            <Icon name="Download" size={13} color="var(--color-muted-foreground)" />
                          </button>
                          <button className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors">
                            <Icon name="MoreHorizontal" size={13} color="var(--color-muted-foreground)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
